import { useState } from "react";
import { MessageSquare, AlertTriangle, Lock, Loader2, Pencil, Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type NoteKind = "admin" | "internal";

type Props = {
  customerNote: string;
  adminNote: string;
  internalNote: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (kind: NoteKind, value: string) => Promise<void>;
};

const STYLES = {
  customer: {
    icon: MessageSquare,
    label: "Napomena kupca",
    tick: "bg-blue-500",
    text: "text-foreground",
    hover: "",
  },
  admin: {
    icon: AlertTriangle,
    label: "Admin napomena",
    tick: "bg-red-500",
    text: "text-red-700 dark:text-red-400",
    hover: "hover:bg-red-500/5",
  },
  internal: {
    icon: Lock,
    label: "Interna napomena",
    tick: "bg-amber-500",
    text: "text-foreground",
    hover: "hover:bg-muted/40",
  },
} as const;

type Variant = keyof typeof STYLES;

function NoteRow({
  variant,
  value,
  editable,
  onEdit,
}: {
  variant: Variant;
  value: string;
  editable: boolean;
  onEdit?: () => void;
}) {
  const s = STYLES[variant];
  const Icon: LucideIcon = s.icon;
  return (
    <div
      className={cn("relative py-2 pl-3 pr-3 transition-colors", editable && s.hover, editable && "cursor-pointer")}
      onClick={editable ? onEdit : undefined}
      title={editable ? "Klikni za uređivanje" : undefined}
    >
      <span aria-hidden className={cn("absolute left-0 top-2.5 h-3.5 w-[2px]", s.tick)} />
      <div className="flex items-center gap-1.5">
        <Icon className={cn("h-3 w-3 shrink-0", s.text)} />
        <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{s.label}</span>
        {editable && <Pencil className="h-2.5 w-2.5 text-muted-foreground/60 ml-auto" />}
      </div>
      <div className={cn("mt-1 text-xs whitespace-pre-wrap", s.text)}>{value}</div>
    </div>
  );
}

function NoteEditor({
  variant,
  value,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  variant: Variant;
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const s = STYLES[variant];
  return (
    <div className="relative py-2 pl-3 pr-3">
      <span aria-hidden className={cn("absolute left-0 top-2.5 h-3.5 w-[2px]", s.tick)} />
      <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{s.label}</span>
      <textarea
        autoFocus
        className="mt-1 w-full text-xs border px-2 py-1.5 min-h-[64px] resize-y bg-background"
        placeholder={
          variant === "admin"
            ? "Vidljiva i u WP adminu, crvenom bojom"
            : "Vidljiva samo timu u ovoj aplikaciji"
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="flex gap-2 mt-1.5">
        <Button size="sm" onClick={onSave} disabled={saving}>
          {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          Spremi
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          Odustani
        </Button>
      </div>
    </div>
  );
}

/** Tri vrste napomena u jednoj kartici. Ranije su bile tri odvojene kartice koje
 *  su zauzimale pola ekrana i kad su prazne — sad se prazne ne prikazuju, a boja
 *  crtice lijevo nosi vrstu. */
export function OrderNotes({ customerNote, adminNote, internalNote, canEdit, saving, onSave }: Props) {
  const [editing, setEditing] = useState<NoteKind | null>(null);
  const [draft, setDraft] = useState("");

  const rows: { variant: Variant; value: string; kind?: NoteKind }[] = [];
  if (customerNote) rows.push({ variant: "customer", value: customerNote });
  if (adminNote) rows.push({ variant: "admin", value: adminNote, kind: "admin" });
  if (internalNote) rows.push({ variant: "internal", value: internalNote, kind: "internal" });

  const missing: { kind: NoteKind; label: string }[] = [];
  if (canEdit && !adminNote) missing.push({ kind: "admin", label: "Admin napomena" });
  if (canEdit && !internalNote) missing.push({ kind: "internal", label: "Interna napomena" });

  if (rows.length === 0 && missing.length === 0) return null;

  function startEdit(kind: NoteKind) {
    setDraft(kind === "admin" ? adminNote : internalNote);
    setEditing(kind);
  }

  async function save() {
    if (!editing) return;
    await onSave(editing, draft);
    setEditing(null);
  }

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="px-3 py-2.5 border-b">
        <span className="text-xs font-medium">Napomene</span>
      </div>

      <div className="divide-y divide-foreground/10">
        {rows.map((r) =>
          editing && r.kind === editing ? (
            <NoteEditor
              key={r.variant}
              variant={r.variant}
              value={draft}
              onChange={setDraft}
              onSave={save}
              onCancel={() => setEditing(null)}
              saving={saving}
            />
          ) : (
            <NoteRow
              key={r.variant}
              variant={r.variant}
              value={r.value}
              editable={canEdit && !!r.kind && !editing}
              onEdit={r.kind ? () => startEdit(r.kind!) : undefined}
            />
          ),
        )}

        {missing.map((m) =>
          editing === m.kind ? (
            <NoteEditor
              key={m.kind}
              variant={m.kind}
              value={draft}
              onChange={setDraft}
              onSave={save}
              onCancel={() => setEditing(null)}
              saving={saving}
            />
          ) : editing ? null : (
            <button
              key={m.kind}
              className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
              onClick={() => startEdit(m.kind)}
            >
              <Plus className="h-3 w-3" />
              {m.label}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
